import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { WarbandMemberDTO } from '../../types';
import { useItemPopover, PopoverItem } from '../../hooks/useItemPopover';
import { formatSoldierType, computeLevelUpStatBonuses } from '../../utils/formatting';
import { groupSoldiersByType } from '../../utils/soldierGrouping';
import { WarbandMemberCard } from '../shared/WarbandMemberCard';
import { CaptainDetails } from '../shared/CaptainDetails';
import { EmptyStateCard } from '../shared/EmptyStateCard';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { gridFlexSx } from '../../App';
import { miniFlexSx } from '../../App';
import { createIceSurface } from '../../theme/surfaces';
import { WarbandBuffsPanel } from '../common/WarbandBuffsPanel';
import { SectionHeading } from '../shared/SectionHeading';
import { WarbandMemberMiniCard } from '../shared/WarbandMemberMiniCard';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';

// ── Memoized per-soldier card ────────────────────────────────────────────────
interface PlaySoldierItemProps {
  soldier: WarbandMemberDTO;
  onItemClick: (e: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  onToggleLeftTable: (member: WarbandMemberDTO) => void;
  levelUpBonuses?: { fight: number; shoot: number; will: number; health: number };
  minified?: boolean;
}

const PlaySoldierItem = React.memo(function PlaySoldierItem({
  soldier,
  onItemClick,
  onToggleLeftTable,
  levelUpBonuses,
  minified
}: PlaySoldierItemProps) {
  const titleMenuItems = useMemo(() => [
    {
      label: soldier.leftTable ? 'Return to Table' : 'Leave Table',
      onClick: () => onToggleLeftTable(soldier),
    },
  ], [soldier, onToggleLeftTable]);

  if (minified) {
    return (
      <WarbandMemberMiniCard
        member={soldier}
        isDefeated={soldier.currentHealth <= 0}
        titleMenuItems={titleMenuItems}
        onItemClick={onItemClick}
        showArmourWhenEmpty
        levelUpBonuses={levelUpBonuses}
      />
    );
  }
  else {
    return (
      <WarbandMemberCard
        member={soldier}
        isDefeated={soldier.currentHealth <= 0}
        titleMenuItems={titleMenuItems}
        onItemClick={onItemClick}
        showArmourWhenEmpty
        showStateToggles
        levelUpBonuses={levelUpBonuses}
      />
    );
  }
});

interface PlaySoldiersTabProps {
  minified?: boolean;
}

export const PlaySoldiersTab: React.FC<PlaySoldiersTabProps> = ({ minified }) => {
  const soldiersData = useWarbandStore((s) => s.soldiersData);
  const wizardData = useWarbandStore((s) => s.wizardData);
  const apprenticeData = useWarbandStore((s) => s.apprenticeData);
  const captainData = useWarbandStore((s) => s.captainData);
  const openCreateSoldierModal = useWarbandStore((s) => s.openCreateSoldierModal);
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const navigateToPage = useWarbandStore((s) => s.navigateToPage);
  const toggleMemberLeftTable = useDirtyTrackingStore((s) => s.toggleMemberLeftTable);

  const { popover, handleItemClick, closePopover } = useItemPopover();
  const [confirmEndGameOpen, setConfirmEndGameOpen] = React.useState(false);

  const levelUps = useWarbandStore((s) => s.levelUps);
  const levelUpBonuses = useMemo(() => computeLevelUpStatBonuses(levelUps), [levelUps]);

  const soldierGroups = useMemo(() => {
    const soldiers = soldiersData?.soldiers ?? [];

    const activeByType = groupSoldiersByType(
      soldiers.filter((soldier) => !soldier.storedInInn && !soldier.badlyWounded && (soldier.currentHealth > 0 && !soldier.leftTable)),
      true
    );

    // Collect all out-of-game members: soldiers, wizard, and apprentice
    const outOfGameMembers = [
      ...soldiers.filter((soldier) => !soldier.storedInInn && !soldier.badlyWounded && (soldier.currentHealth <= 0 || soldier.leftTable)),
      ...(wizardData && (!wizardData.storedInInn && !wizardData.badlyWounded && (wizardData.currentHealth <= 0 || wizardData.leftTable)) ? [wizardData] : []),
      ...(apprenticeData && (!apprenticeData.storedInInn && !apprenticeData.badlyWounded && (apprenticeData.currentHealth <= 0 || apprenticeData.leftTable)) ? [apprenticeData] : []),
      ...(captainData && (!captainData.storedInInn && !captainData.badlyWounded && (captainData.currentHealth <= 0 || captainData.leftTable)) ? [captainData] : []),
    ].sort((left, right) => left.name.localeCompare(right.name));

    const standardGroup = activeByType.find((group) => group.type === 'StandardSoldier');
    const specialistGroup = activeByType.find((group) => group.type === 'SpecialistSoldier');
    const temporaryGroup = activeByType.find((group) => group.type === 'Temporary');

    const remainingGroups = activeByType
      .filter((group) => group.type !== 'StandardSoldier' && group.type !== 'SpecialistSoldier' && group.type !== 'Temporary')
      .map((group) => ({
        key: group.type,
        label: formatSoldierType(group.type, true),
        members: group.members,
      }));

    const orderedGroups = [
      ...(standardGroup
        ? [{ key: standardGroup.type, label: 'Standard Soldiers', members: standardGroup.members }]
        : []),
      ...(specialistGroup
        ? [{ key: specialistGroup.type, label: 'Specialist Soldiers', members: specialistGroup.members }]
        : []),
      ...remainingGroups,
      ...(temporaryGroup
        ? [{ key: 'Temporary', label: 'Temporary Soldiers', members: temporaryGroup.members }]
        : [{ key: 'Temporary', label: 'Temporary Soldiers', members: [] }]),
      ...(outOfGameMembers.length > 0
        ? [{ key: 'OutOfGame', label: 'Out of the game', members: outOfGameMembers }]
        : []),
    ];

    return orderedGroups;
  }, [soldiersData, wizardData, apprenticeData, captainData]);

  const handleToggleWizardLeftTable = useCallback(() => {
    if (!wizardData?.id) return;
    toggleMemberLeftTable(wizardData.id, 'wizard');
  }, [wizardData?.id, toggleMemberLeftTable]);

  const handleToggleApprenticeLeftTable = useCallback(() => {
    if (!apprenticeData?.id) return;
    toggleMemberLeftTable(apprenticeData.id, 'apprentice');
  }, [apprenticeData?.id, toggleMemberLeftTable]);

  const handleToggleCaptainLeftTable = useCallback(() => {
    if (!captainData?.id) return;
    toggleMemberLeftTable(captainData.id, 'captain');
  }, [captainData?.id, toggleMemberLeftTable]);

  const handleToggleAnyMemberLeftTable = useCallback((member: WarbandMemberDTO) => {
    if (wizardData?.id && member.id === wizardData.id) {
      toggleMemberLeftTable(wizardData.id, 'wizard');
    } else if (apprenticeData?.id && member.id === apprenticeData.id) {
      toggleMemberLeftTable(apprenticeData.id, 'apprentice');
    } else if (captainData?.id && member.id === captainData.id) {
      toggleMemberLeftTable(captainData.id, 'captain');
    } else {
      toggleMemberLeftTable(member.id, 'soldier');
    }
  }, [wizardData?.id, apprenticeData?.id, captainData?.id, toggleMemberLeftTable]);

  const wizardMenuItems = useMemo(() => wizardData ? [
    { label: wizardData?.leftTable ? 'Return to Table' : 'Leave Table', onClick: handleToggleWizardLeftTable },
  ] : [], [wizardData, handleToggleWizardLeftTable]);

  const apprenticeMenuItems = useMemo(() => apprenticeData ? [
    { label: apprenticeData?.leftTable ? 'Return to Table' : 'Leave Table', onClick: handleToggleApprenticeLeftTable },
  ] : [], [apprenticeData, handleToggleApprenticeLeftTable]);

  const captainMenuItems = useMemo(() => captainData ? [
    { label: captainData?.leftTable ? 'Return to Table' : 'Leave Table', onClick: handleToggleCaptainLeftTable },
  ] : [], [captainData, handleToggleCaptainLeftTable]);

  const boxSx = minified ? miniFlexSx : gridFlexSx;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
        <Button variant="contained" onClick={() => setConfirmEndGameOpen(true)}>End Game</Button>
      </Box>
      <ConfirmActionDialog
        open={confirmEndGameOpen}
        title="End game"
        message="End the game and move to the post-game sequence?"
        confirmLabel="End Game"
        onClose={() => setConfirmEndGameOpen(false)}
        onConfirm={() => {
          setConfirmEndGameOpen(false);
          navigateToPage('postgame');
        }}
      />
      <Box sx={createIceSurface(10, { icicles: true, card: true })} p={1.5}>
        <WarbandBuffsPanel />
      </Box>
      <Box sx={createIceSurface(11, { icicles: true, card: true })} p={1.5}>
        {/* Wizard Section */}
        <SectionHeading label="Spellcasters" />
        <Box sx={boxSx}>
          {/* Wizard Section */}
          {wizardData && wizardData.currentHealth > 0 && !wizardData.leftTable && !wizardData.badlyWounded && (
            minified ? (
              <WarbandMemberMiniCard
                member={wizardData}
                isDefeated={wizardData.currentHealth <= 0}
                titleMenuItems={apprenticeMenuItems}
                levelUpBonuses={levelUpBonuses}
                onItemClick={handleItemClick}
              />
            ) : (
              <WarbandMemberCard
                member={wizardData}
                isDefeated={wizardData.currentHealth <= 0}
                titleMenuItems={wizardMenuItems}
                onItemClick={handleItemClick}
                showStateToggles
                levelUpBonuses={levelUpBonuses}
              />
            )
          )}

          {/* Apprentice Section */}
          {apprenticeData && apprenticeData.currentHealth > 0 && !apprenticeData.leftTable && !apprenticeData.badlyWounded && (
            minified ? (
              <WarbandMemberMiniCard
                member={apprenticeData}
                isDefeated={apprenticeData.currentHealth <= 0}
                titleMenuItems={apprenticeMenuItems}
                levelUpBonuses={levelUpBonuses}
                onItemClick={handleItemClick}
              />
            ) : (
              <WarbandMemberCard
                member={apprenticeData}
                isDefeated={apprenticeData.currentHealth <= 0}
                titleMenuItems={apprenticeMenuItems}
                onItemClick={handleItemClick}
                showStateToggles
                levelUpBonuses={levelUpBonuses}
              />
            )
          )}
        </Box>
      </Box>

      {/* Captain Section */}
      {captainData && captainData.currentHealth > 0 && !captainData.leftTable && !captainData.badlyWounded && !captainData.storedInInn && (
        <Box sx={createIceSurface(13, { icicles: true, card: true })} p={1.5}>
          <SectionHeading label="Captain" />
          <Box sx={boxSx}>
            <Box>
              {minified ? (
                <WarbandMemberMiniCard
                  member={captainData}
                  isDefeated={captainData.currentHealth <= 0}
                  titleMenuItems={captainMenuItems}
                  onItemClick={handleItemClick}
                  showArmourWhenEmpty
                />
              ) : (
                <WarbandMemberCard
                  member={captainData}
                  isDefeated={captainData.currentHealth <= 0}
                  titleMenuItems={captainMenuItems}
                  onItemClick={handleItemClick}
                  showArmourWhenEmpty
                  showStateToggles
                  extraDetails={<CaptainDetails captain={captainData} />}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Soldiers Section */}
      {
        soldierGroups.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {soldierGroups.map((group, groupIdx) => (
              <Box sx={createIceSurface(12, { icicles: true, card: true })} p={1.5} key={group.key}>
                <Box>
                  <SectionHeading
                    label={group.label}
                    action={group.key === 'Temporary' ? (
                      <IconButton size="small" onClick={() => openCreateSoldierModal(true)}>
                        <AddIcon />
                      </IconButton>
                    ) : undefined}
                  />
                  <Box sx={boxSx}>
                    {group.members.map((soldier) => (
                      <PlaySoldierItem
                        key={soldier.id}
                        soldier={soldier}
                        onItemClick={handleItemClick}
                        onToggleLeftTable={handleToggleAnyMemberLeftTable}
                        levelUpBonuses={
                          (wizardData?.id === soldier.id || apprenticeData?.id === soldier.id)
                            ? levelUpBonuses
                            : undefined
                        }
                        minified={minified}
                      />
                    ))}

                  </Box>

                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyStateCard message="No soldiers in this party" />
        )
      }

      <ItemDetailsPopover popover={popover} onClose={closePopover} />
    </Box >
  );
};
